import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  BaseMenu,
  BaseMenuCategory,
  BaseMenuItem,
  BaseMenuListResponse,
  BaseMenuCategoryListResponse,
  BaseMenuItemListResponse,
  MenuCustomFieldDTO,
  GetMenuCustomFieldsResponse,
} from "../../models/BaseMenu.model";
import {
  getBaseMenus,
  createOrUpdateBaseMenu,
  deleteBaseMenu,
  getBaseMenuCategories,
  createOrUpdateBaseMenuCategory,
  deleteBaseMenuCategory,
  getBaseMenuItems,
  createOrUpdateBaseMenuItem,
  deleteBaseMenuItem,
  createMenuCustomField,
  getMenuCustomFields,
  deleteMenuCustomField,
} from "../thunks/baseMenuThunks";

interface BaseMenuState {
  // תפריטי בסיס
  menus: BaseMenu[];
  menusLoading: boolean;
  menusError: string | null;

  // קטגוריות תפריט נוכחי
  currentMenuId: number | null;
  currentMenuName: string;
  categories: BaseMenuCategory[];
  categoriesLoading: boolean;
  categoriesError: string | null;

  // פריטי קטגוריה נוכחית
  currentCategoryId: number | null;
  currentCategoryName: string;
  items: BaseMenuItem[];
  itemsLoading: boolean;
  itemsError: string | null;

  // פעולות CRUD
  operationLoading: boolean;
  operationError: string | null;
  operationSuccess: boolean;
  operationMessage: string;

  // שדות מותאמים אישית
  customFields: MenuCustomFieldDTO[];
  customFieldsLoading: boolean;
  customFieldsError: string | null;

  // כללי
  initialized: boolean;
}

const initialState: BaseMenuState = {
  menus: [],
  menusLoading: false,
  menusError: null,

  currentMenuId: null,
  currentMenuName: "",
  categories: [],
  categoriesLoading: false,
  categoriesError: null,

  currentCategoryId: null,
  currentCategoryName: "",
  items: [],
  itemsLoading: false,
  itemsError: null,

  operationLoading: false,
  operationError: null,
  operationSuccess: false,
  operationMessage: "",

  customFields: [],
  customFieldsLoading: false,
  customFieldsError: null,

  initialized: false,
};

const baseMenuSlice = createSlice({
  name: "baseMenu",
  initialState,
  reducers: {
    clearMessages: (state) => {
      state.menusError = null;
      state.categoriesError = null;
      state.itemsError = null;
      state.operationError = null;
      state.operationMessage = "";
      state.operationSuccess = false;
      state.customFieldsError = null;
    },
    resetBaseMenuState: (state) => {
      Object.assign(state, initialState);
    },
    clearCurrentMenu: (state) => {
      state.currentMenuId = null;
      state.currentMenuName = "";
      state.categories = [];
      state.categoriesError = null;
      state.currentCategoryId = null;
      state.currentCategoryName = "";
      state.items = [];
      state.itemsError = null;
    },
    setCurrentMenu: (
      state,
      action: PayloadAction<{ menuId: number; menuName: string }>
    ) => {
      state.currentMenuId = action.payload.menuId;
      state.currentMenuName = action.payload.menuName;
      state.categories = [];
      state.categoriesError = null;
      state.currentCategoryId = null;
      state.currentCategoryName = "";
      state.items = [];
      state.itemsError = null;
    },
    clearCurrentCategory: (state) => {
      state.currentCategoryId = null;
      state.currentCategoryName = "";
      state.items = [];
      state.itemsError = null;
    },
    setCurrentCategory: (
      state,
      action: PayloadAction<{ categoryId: number; categoryName: string }>
    ) => {
      state.currentCategoryId = action.payload.categoryId;
      state.currentCategoryName = action.payload.categoryName;
      state.items = [];
      state.itemsError = null;
    },
    clearMenusOnCompanyChange: (state) => {
      state.menus = [];
      state.currentMenuId = null;
      state.currentMenuName = "";
      state.categories = [];
      state.currentCategoryId = null;
      state.currentCategoryName = "";
      state.items = [];
      state.initialized = false;
      state.menusError = null;
      state.categoriesError = null;
      state.itemsError = null;
    },
    clearCustomFieldsOnCompanyChange: (state) => {
      state.customFields = [];
      state.customFieldsError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get Base Menus
      .addCase(getBaseMenus.pending, (state) => {
        state.menusLoading = true;
        state.menusError = null;
      })
      .addCase(
        getBaseMenus.fulfilled,
        (state, action: PayloadAction<BaseMenuListResponse>) => {
          state.menusLoading = false;
          state.menus = action.payload.data;
          state.initialized = true;
        }
      )
      .addCase(getBaseMenus.rejected, (state, action) => {
        state.menusLoading = false;
        state.menusError =
          (action.payload as string) || "Failed to fetch base menus";
      })

      // Create/Update Base Menu
      .addCase(createOrUpdateBaseMenu.pending, (state) => {
        state.operationLoading = true;
        state.operationError = null;
        state.operationSuccess = false;
      })
      .addCase(createOrUpdateBaseMenu.fulfilled, (state, action) => {
        state.operationLoading = false;
        state.operationSuccess = true;
        state.operationMessage = action.payload.message;

        const requestData = action.meta.arg;
        const isUpdate = requestData.baseMenuID > 0;

        if (isUpdate) {
          // עדכון תפריט קיים
          const index = state.menus.findIndex(
            (m) => m.baseMenu_ID === requestData.baseMenuID
          );
          if (index !== -1) {
            const updatedMenu: BaseMenu = {
              baseMenu_ID: requestData.baseMenuID,
              baseMenu_Name: requestData.menuName,
              baseMenu_Description: requestData.menuDescription,
              baseMenu_Image: requestData.imageURL,
              baseMenu_IsDefault: requestData.isDefault,
            };

            // אם זה ברירת מחדל חדשה, הסר מכל השאר
            if (updatedMenu.baseMenu_IsDefault) {
              state.menus = state.menus.map((m) =>
                m.baseMenu_ID !== updatedMenu.baseMenu_ID
                  ? { ...m, baseMenu_IsDefault: false }
                  : updatedMenu
              );
            } else {
              state.menus[index] = updatedMenu;
            }
          }
        } else {
          // הוספת תפריט חדש
          const newMenu: BaseMenu = {
            baseMenu_ID: action.payload.result, // מהשרת
            baseMenu_Name: requestData.menuName,
            baseMenu_Description: requestData.menuDescription,
            baseMenu_Image: requestData.imageURL,
            baseMenu_IsDefault: requestData.isDefault,
          };

          // אם זה ברירת מחדל, הסר מכל השאר
          if (newMenu.baseMenu_IsDefault) {
            state.menus = state.menus.map((m) => ({
              ...m,
              baseMenu_IsDefault: false,
            }));
          }

          state.menus.push(newMenu);
        }
      })
      .addCase(createOrUpdateBaseMenu.rejected, (state, action) => {
        state.operationLoading = false;
        state.operationSuccess = false;
        state.operationError =
          (action.payload as string) || "Failed to create/update menu";
      })

      // Delete Base Menu
      .addCase(deleteBaseMenu.pending, (state) => {
        state.operationLoading = true;
        state.operationError = null;
        state.operationSuccess = false;
      })
      .addCase(deleteBaseMenu.fulfilled, (state, action) => {
        state.operationLoading = false;
        state.operationSuccess = true;
        state.operationMessage = action.payload.message;

        const requestData = action.meta.arg;
        const deletedMenuId = requestData.baseMenuID;

        // הסרה מהמערך
        state.menus = state.menus.filter(
          (m) => m.baseMenu_ID !== deletedMenuId
        );

        // אם זה התפריט הנוכחי, נקה אותו
        if (state.currentMenuId === deletedMenuId) {
          state.currentMenuId = null;
          state.currentMenuName = "";
          state.categories = [];
          state.currentCategoryId = null;
          state.currentCategoryName = "";
          state.items = [];
        }
      })
      .addCase(deleteBaseMenu.rejected, (state, action) => {
        state.operationLoading = false;
        state.operationSuccess = false;
        state.operationError =
          (action.payload as string) || "Failed to delete menu";
      })

      // Get Menu Categories
      .addCase(getBaseMenuCategories.pending, (state) => {
        state.categoriesLoading = true;
        state.categoriesError = null;
      })
      .addCase(
        getBaseMenuCategories.fulfilled,
        (state, action: PayloadAction<BaseMenuCategoryListResponse>) => {
          state.categoriesLoading = false;
          state.categories = action.payload.data;
        }
      )
      .addCase(getBaseMenuCategories.rejected, (state, action) => {
        state.categoriesLoading = false;
        state.categoriesError =
          (action.payload as string) || "Failed to fetch menu categories";
      })

      // Create/Update Menu Category
      .addCase(createOrUpdateBaseMenuCategory.pending, (state) => {
        state.operationLoading = true;
        state.operationError = null;
        state.operationSuccess = false;
      })
      .addCase(createOrUpdateBaseMenuCategory.fulfilled, (state, action) => {
        state.operationLoading = false;
        state.operationSuccess = true;
        state.operationMessage = action.payload.message;

        const requestData = action.meta.arg;
        const isUpdate = requestData.menuCategoryID > 0;

        if (isUpdate) {
          // עדכון קטגוריה קיימת
          const index = state.categories.findIndex(
            (c) => c.baseMenuCategory_ID === requestData.menuCategoryID
          );
          if (index !== -1) {
            const updatedCategory: BaseMenuCategory = {
              baseMenuCategory_ID: requestData.menuCategoryID,
              baseMenuCategory_Name: requestData.categoryName,
              baseMenuCategory_Description: requestData.categoryDescription,
              baseMenuCategory_Image: requestData.categoryImageURL,
              baseMenuCategory_MaxItemToSelect: requestData.categoryMaxItems,
              baseMenuCategory_ShowToCustomer: requestData.categoryVisible,
            };

            state.categories[index] = updatedCategory;
          }
        } else {
          // הוספת קטגוריה חדשה
          const newCategory: BaseMenuCategory = {
            baseMenuCategory_ID: action.payload.result, // מהשרת
            baseMenuCategory_Name: requestData.categoryName,
            baseMenuCategory_Description: requestData.categoryDescription,
            baseMenuCategory_Image: requestData.categoryImageURL,
            baseMenuCategory_MaxItemToSelect: requestData.categoryMaxItems,
            baseMenuCategory_ShowToCustomer: requestData.categoryVisible,
          };

          state.categories.push(newCategory);
        }
      })
      .addCase(createOrUpdateBaseMenuCategory.rejected, (state, action) => {
        state.operationLoading = false;
        state.operationSuccess = false;
        state.operationError =
          (action.payload as string) || "Failed to create/update category";
      })

      // Delete Menu Category
      .addCase(deleteBaseMenuCategory.pending, (state) => {
        state.operationLoading = true;
        state.operationError = null;
        state.operationSuccess = false;
      })
      .addCase(deleteBaseMenuCategory.fulfilled, (state, action) => {
        state.operationLoading = false;
        state.operationSuccess = true;
        state.operationMessage = action.payload.message;

        const requestData = action.meta.arg;
        const deletedCategoryId = requestData.menuCategoryID;

        // הסרה מהמערך
        state.categories = state.categories.filter(
          (c) => c.baseMenuCategory_ID !== deletedCategoryId
        );

        // אם זו הקטגוריה הנוכחית, נקה אותה
        if (state.currentCategoryId === deletedCategoryId) {
          state.currentCategoryId = null;
          state.currentCategoryName = "";
          state.items = [];
        }
      })
      .addCase(deleteBaseMenuCategory.rejected, (state, action) => {
        state.operationLoading = false;
        state.operationSuccess = false;
        state.operationError =
          (action.payload as string) || "Failed to delete category";
      })

      // Get Menu Items
      .addCase(getBaseMenuItems.pending, (state) => {
        state.itemsLoading = true;
        state.itemsError = null;
      })
      .addCase(
        getBaseMenuItems.fulfilled,
        (state, action: PayloadAction<BaseMenuItemListResponse>) => {
          state.itemsLoading = false;
          state.items = action.payload.data;
        }
      )
      .addCase(getBaseMenuItems.rejected, (state, action) => {
        state.itemsLoading = false;
        state.itemsError =
          (action.payload as string) || "Failed to fetch menu items";
      })

      // Create/Update Menu Item
      .addCase(createOrUpdateBaseMenuItem.pending, (state) => {
        state.operationLoading = true;
        state.operationError = null;
        state.operationSuccess = false;
      })
      .addCase(createOrUpdateBaseMenuItem.fulfilled, (state, action) => {
        state.operationLoading = false;
        state.operationSuccess = true;
        state.operationMessage = action.payload.message;

        const requestData = action.meta.arg;
        const isUpdate = requestData.menuItemID > 0;

        if (isUpdate) {
          // עדכון פריט קיים
          const index = state.items.findIndex(
            (i) => i.baseMenuItem_ID === requestData.menuItemID
          );
          if (index !== -1) {
            const updatedItem: BaseMenuItem = {
              baseMenuItem_ID: requestData.menuItemID,
              baseMenuItem_CategoryID: requestData.menuCategoryID,
              baseMenuItem_Name: requestData.itemName,
              baseMenuItem_Description: requestData.itemDescription,
              baseMenuItem_Image: requestData.itemImageURL,
              baseMenuItem_Tags: requestData.itemTags,
              baseMenuItem_AllergansJSON: requestData.itemAllergansJSON,
              baseMenuItem_Seasonality: requestData.itemIsSeasonality,
              baseMenuItem_SpicinessLevel: requestData.itemSpicinessLevel,
              baseMenuItem_ChildrenFriendly: requestData.itemIsChildrenFriendly,
              baseMenuItem_IsExtraCost: requestData.itemIsExtraCost,
              baseMenuItem_ExtraCostPerHead: requestData.itemExtraCostPerHead,
              baseMenuItem_FoodCost: requestData.itemFoodCost,
              baseMenuItem_QtyPercent: requestData.qtyPercent,
            };

            state.items[index] = updatedItem;
          }
        } else {
          // הוספת פריט חדש
          const newItem: BaseMenuItem = {
            baseMenuItem_ID: action.payload.result, // מהשרת
            baseMenuItem_CategoryID: requestData.menuCategoryID,
            baseMenuItem_Name: requestData.itemName,
            baseMenuItem_Description: requestData.itemDescription,
            baseMenuItem_Image: requestData.itemImageURL,
            baseMenuItem_Tags: requestData.itemTags,
            baseMenuItem_AllergansJSON: requestData.itemAllergansJSON,
            baseMenuItem_Seasonality: requestData.itemIsSeasonality,
            baseMenuItem_SpicinessLevel: requestData.itemSpicinessLevel,
            baseMenuItem_ChildrenFriendly: requestData.itemIsChildrenFriendly,
            baseMenuItem_IsExtraCost: requestData.itemIsExtraCost,
            baseMenuItem_ExtraCostPerHead: requestData.itemExtraCostPerHead,
            baseMenuItem_FoodCost: requestData.itemFoodCost,
            baseMenuItem_QtyPercent: requestData.qtyPercent,
          };

          state.items.push(newItem);
        }
      })
      .addCase(createOrUpdateBaseMenuItem.rejected, (state, action) => {
        state.operationLoading = false;
        state.operationSuccess = false;
        state.operationError =
          (action.payload as string) || "Failed to create/update item";
      })

      // Delete Menu Item
      .addCase(deleteBaseMenuItem.pending, (state) => {
        state.operationLoading = true;
        state.operationError = null;
        state.operationSuccess = false;
      })
      .addCase(deleteBaseMenuItem.fulfilled, (state, action) => {
        state.operationLoading = false;
        state.operationSuccess = true;
        state.operationMessage = action.payload.message;

        const requestData = action.meta.arg;
        const deletedItemId = requestData.menuItemID;

        // הסרה מהמערך
        state.items = state.items.filter(
          (i) => i.baseMenuItem_ID !== deletedItemId
        );
      })
      .addCase(deleteBaseMenuItem.rejected, (state, action) => {
        state.operationLoading = false;
        state.operationSuccess = false;
        state.operationError =
          (action.payload as string) || "Failed to delete item";
      })

      // Get Custom Fields
      .addCase(getMenuCustomFields.pending, (state) => {
        state.customFieldsLoading = true;
        state.customFieldsError = null;
      })
      .addCase(
        getMenuCustomFields.fulfilled,
        (state, action: PayloadAction<GetMenuCustomFieldsResponse>) => {
          state.customFieldsLoading = false;
          state.customFields = action.payload.menuCustomFields;
        }
      )
      .addCase(getMenuCustomFields.rejected, (state, action) => {
        state.customFieldsLoading = false;
        state.customFieldsError =
          (action.payload as string) || "Failed to fetch custom fields";
      })

      // Create/Update Custom Field
      .addCase(createMenuCustomField.pending, (state) => {
        state.operationLoading = true;
        state.operationError = null;
        state.operationSuccess = false;
      })
      .addCase(createMenuCustomField.fulfilled, (state, action) => {
        state.operationLoading = false;
        state.operationSuccess = true;
        state.operationMessage = action.payload.message;

        const requestData = action.meta.arg;
        const isUpdate = requestData.menuCustomFieldID > 0;

        if (isUpdate) {
          // עדכון שדה קיים
          const index = state.customFields.findIndex(
            (f) => f.customField_ID === requestData.menuCustomFieldID
          );
          if (index !== -1) {
            const updatedField: MenuCustomFieldDTO = {
              customField_ID: requestData.menuCustomFieldID,
              label: requestData.fieldLabel,
              fieldType: requestData.fieldType,
              options: requestData.fieldOptions,
              maxLen: requestData.fieldMaxLen,
              defaultValue: requestData.fieldDefualtVal,
              isRequired: requestData.fieldRequired,
              editableByCustomer: requestData.editableByCustomer,
              saveChangesLog: requestData.saveChangesLog,
            };

            state.customFields[index] = updatedField;
          }
        } else {
          // הוספת שדה חדש
          const newField: MenuCustomFieldDTO = {
            customField_ID: action.payload.menuCustomFieldID, // מהשרת
            label: requestData.fieldLabel,
            fieldType: requestData.fieldType,
            options: requestData.fieldOptions,
            maxLen: requestData.fieldMaxLen,
            defaultValue: requestData.fieldDefualtVal,
            isRequired: requestData.fieldRequired,
            editableByCustomer: requestData.editableByCustomer,
            saveChangesLog: requestData.saveChangesLog,
          };

          state.customFields.push(newField);
        }
      })
      .addCase(createMenuCustomField.rejected, (state, action) => {
        state.operationLoading = false;
        state.operationSuccess = false;
        state.operationError =
          (action.payload as string) || "Failed to create/update custom field";
      })

      // Delete Custom Field
      .addCase(deleteMenuCustomField.pending, (state) => {
        state.operationLoading = true;
        state.operationError = null;
        state.operationSuccess = false;
      })
      .addCase(deleteMenuCustomField.fulfilled, (state, action) => {
        state.operationLoading = false;
        state.operationSuccess = true;
        state.operationMessage = action.payload.message;

        const requestData = action.meta.arg;
        const deletedFieldId = requestData.menuCustomFieldID;

        // הסרה מהמערך
        state.customFields = state.customFields.filter(
          (f) => f.customField_ID !== deletedFieldId
        );
      })
      .addCase(deleteMenuCustomField.rejected, (state, action) => {
        state.operationLoading = false;
        state.operationSuccess = false;
        state.operationError =
          (action.payload as string) || "Failed to delete custom field";
      });
  },
});

export const {
  clearMessages,
  resetBaseMenuState,
  clearCurrentMenu,
  setCurrentMenu,
  clearCurrentCategory,
  setCurrentCategory,
  clearMenusOnCompanyChange,
  clearCustomFieldsOnCompanyChange,
} = baseMenuSlice.actions;

export default baseMenuSlice.reducer;

// Selectors
export const selectBaseMenus = (state: { baseMenu: BaseMenuState }) =>
  state.baseMenu.menus;
export const selectMenusLoading = (state: { baseMenu: BaseMenuState }) =>
  state.baseMenu.menusLoading;
export const selectMenusError = (state: { baseMenu: BaseMenuState }) =>
  state.baseMenu.menusError;
export const selectMenusInitialized = (state: { baseMenu: BaseMenuState }) =>
  state.baseMenu.initialized;

export const selectCurrentMenuId = (state: { baseMenu: BaseMenuState }) =>
  state.baseMenu.currentMenuId;
export const selectCurrentMenuName = (state: { baseMenu: BaseMenuState }) =>
  state.baseMenu.currentMenuName;
export const selectMenuCategories = (state: { baseMenu: BaseMenuState }) =>
  state.baseMenu.categories;
export const selectCategoriesLoading = (state: { baseMenu: BaseMenuState }) =>
  state.baseMenu.categoriesLoading;
export const selectCategoriesError = (state: { baseMenu: BaseMenuState }) =>
  state.baseMenu.categoriesError;

export const selectCurrentCategoryId = (state: { baseMenu: BaseMenuState }) =>
  state.baseMenu.currentCategoryId;
export const selectCurrentCategoryName = (state: { baseMenu: BaseMenuState }) =>
  state.baseMenu.currentCategoryName;
export const selectMenuItems = (state: { baseMenu: BaseMenuState }) =>
  state.baseMenu.items;
export const selectItemsLoading = (state: { baseMenu: BaseMenuState }) =>
  state.baseMenu.itemsLoading;
export const selectItemsError = (state: { baseMenu: BaseMenuState }) =>
  state.baseMenu.itemsError;

export const selectOperationLoading = (state: { baseMenu: BaseMenuState }) =>
  state.baseMenu.operationLoading;
export const selectOperationError = (state: { baseMenu: BaseMenuState }) =>
  state.baseMenu.operationError;
export const selectOperationSuccess = (state: { baseMenu: BaseMenuState }) =>
  state.baseMenu.operationSuccess;
export const selectOperationMessage = (state: { baseMenu: BaseMenuState }) =>
  state.baseMenu.operationMessage;

// Computed selectors
export const selectCurrentMenuHasCategories = (state: {
  baseMenu: BaseMenuState;
}) => state.baseMenu.categories.length > 0;

export const selectCurrentCategoryHasItems = (state: {
  baseMenu: BaseMenuState;
}) => state.baseMenu.items.length > 0;

export const selectMenuById =
  (menuId: number) => (state: { baseMenu: BaseMenuState }) =>
    state.baseMenu.menus.find((menu) => menu.baseMenu_ID === menuId);

export const selectCategoryById =
  (categoryId: number) => (state: { baseMenu: BaseMenuState }) =>
    state.baseMenu.categories.find(
      (category) => category.baseMenuCategory_ID === categoryId
    );

export const selectItemById =
  (itemId: number) => (state: { baseMenu: BaseMenuState }) =>
    state.baseMenu.items.find((item) => item.baseMenuItem_ID === itemId);

export const selectDefaultMenu = (state: { baseMenu: BaseMenuState }) =>
  state.baseMenu.menus.find((menu) => menu.baseMenu_IsDefault);

export const selectVisibleCategories = (state: { baseMenu: BaseMenuState }) =>
  state.baseMenu.categories.filter(
    (category) => category.baseMenuCategory_ShowToCustomer
  );

export const selectCategoriesByMenuId =
  (menuId: number) => (state: { baseMenu: BaseMenuState }) =>
    state.baseMenu.categories.filter(
      (category) => category.baseMenuCategory_ID === menuId
    );

export const selectItemsByCategoryId =
  (categoryId: number) => (state: { baseMenu: BaseMenuState }) =>
    state.baseMenu.items.filter(
      (item) => item.baseMenuItem_CategoryID === categoryId
    );

// Selectors - הוספה
export const selectCustomFields = (state: { baseMenu: BaseMenuState }) =>
  state.baseMenu.customFields;
export const selectCustomFieldsLoading = (state: { baseMenu: BaseMenuState }) =>
  state.baseMenu.customFieldsLoading;
export const selectCustomFieldsError = (state: { baseMenu: BaseMenuState }) =>
  state.baseMenu.customFieldsError;

export const selectCustomFieldById =
  (fieldId: number) => (state: { baseMenu: BaseMenuState }) =>
    state.baseMenu.customFields.find(
      (field) => field.customField_ID === fieldId
    );

export const selectRequiredCustomFields = (state: {
  baseMenu: BaseMenuState;
}) => state.baseMenu.customFields.filter((field) => field.isRequired);

export const selectEditableCustomFields = (state: {
  baseMenu: BaseMenuState;
}) => state.baseMenu.customFields.filter((field) => field.editableByCustomer);
